import * as React from 'react';
import { NavMain } from './nav-main';
import { NavCollapsible } from './nav-collapsible';
import { NavSecondary } from './nav-secondary';
import { NavUser } from './nav-user';
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuItem,
} from '../ui/sidebar';
import {
    MdOutlineSpaceDashboard,
    MdOutlineHandshake,
    MdCreditCard,
    MdFolderOpen,
} from 'react-icons/md';
import { RiUserLocationLine } from 'react-icons/ri';
import {
    IoDocumentOutline,
    IoMailUnreadOutline,
    IoSettingsOutline,
    IoHelpBuoyOutline,
} from 'react-icons/io5';
import { GiBackwardTime } from 'react-icons/gi';
import { VscGraph } from 'react-icons/vsc';
import { BiBuildings } from 'react-icons/bi';
import { UserContext } from '../../context/userContext';
import { ReactComponent as Logo } from '../../assets/Leasehub.svg?react';
import { useEffect } from 'react';
import { useState } from 'react';
import { useCallback } from 'react';
import { ApiContext } from '../../context/apiContext';
import { useContext } from 'react';
import { SocketContext } from '../../context/socketContext';
import { useLocation } from 'react-router-dom';
import {
    Calendar,
    ClipboardList,
    LineChart,
    Shuffle,
    UsersRound,
    Workflow,
    Headset,
} from 'lucide-react';
import { openBrevoChat } from '../../utils/brevo';

// Primary user (agent / admin) sidebar structure.
//
// Goal: avoid scroll by collapsing low-frequency groups behind expandable
// parent rows ("nav within nav"). Frequently-used destinations stay
// permanently visible at the top; everything else lives in a collapsible
// group that opens automatically when the user is on one of its children.
//
// If you're adding a new page, decide:
//   - is it used every visit? → add it to `topNav`
//   - is it part of a clear workflow with other pages? → add to the matching
//     collapsible group (or create a new one)
//
export const URL_MAP = {
    // Dashboard is the home page — kept flat at the top because it's where
    // people land. Everything else lives inside a collapsible group.
    topNav: [
        {
            title: 'Dashboard',
            url: '/dashboard',
            header: 'Dashboard',
            icon: MdOutlineSpaceDashboard,
        },
    ],
    // Collapsible groups: parent row + children list. The parent row is
    // clickable to expand/collapse; nothing in `children` is shown until the
    // parent is opened (auto-opens if any child matches the current route).
    leadsGroup: {
        title: 'Leads & CRM',
        icon: UsersRound,
        children: [
            { title: 'Leads', url: '/leads', header: 'All Leads', icon: UsersRound },
            {
                title: 'Pre-qual Form',
                url: '/leads/pre-qualification',
                header: 'Pre-qualification',
                icon: ClipboardList,
            },
            {
                title: 'Lead Assignment',
                url: '/leads/assignment',
                header: 'Lead Assignment',
                icon: Shuffle,
            },
            {
                title: 'Personal Schedule',
                url: '/scheduling',
                header: 'Personal Schedule',
                icon: Calendar,
            },
        ],
    },
    // Pipeline: lead-to-renewal lifecycle (post-CRM).
    pipelineGroup: {
        title: 'Pipeline',
        icon: Workflow,
        children: [
            {
                title: 'Showings',
                url: '/showings',
                header: 'Showings',
                icon: RiUserLocationLine,
            },
            {
                title: 'Applications',
                url: '/applications',
                header: 'Applications',
                icon: IoDocumentOutline,
            },
            { title: 'Deals', url: '/deals', header: 'All Deals', icon: MdOutlineHandshake },
            {
                title: 'Renewals',
                url: '/renewals',
                header: 'Upcoming Renewals',
                icon: GiBackwardTime,
            },
        ],
    },
    // Inventory: anything you own / file against a unit. Documents live here
    // because they're records attached to properties (leases, contracts,
    // signed agreements) — not conversations.
    inventoryGroup: {
        title: 'Properties',
        url: '/properties',
        header: 'All Properties',
        icon: BiBuildings,
    },
    reportsGroup: {
        title: 'Reports & Payments',
        icon: LineChart,
        children: [
            { title: 'Insights', url: '/insights', header: 'Insights', icon: VscGraph },
            { title: 'Payments', url: '/payments', header: 'Payments', icon: MdCreditCard },
        ],
    },
    messagingNav: [
        {
            title: 'Messaging',
            url: '/messaging',
            header: 'All Messages',
            icon: IoMailUnreadOutline,
        },
    ],
    // Legacy flat arrays kept for non-primary user types (landlord, accountant,
    // internal_agent) that still render via NavMain. Don't add new items here —
    // either extend the flow above or wire those user types into the same
    // grouped pattern.
    navMain: [
        {
            title: 'Dashboard',
            url: '/dashboard',
            header: 'Dashboard',
            icon: MdOutlineSpaceDashboard,
        },
        {
            title: 'Showings',
            url: '/showings',
            header: 'Showings',
            icon: RiUserLocationLine,
        },
        {
            title: 'Applications',
            url: '/applications',
            header: 'Applications',
            icon: IoDocumentOutline,
        },
        {
            title: 'Deals',
            url: '/deals',
            header: 'All Deals',
            icon: MdOutlineHandshake,
        },
        {
            title: 'Renewals',
            url: '/renewals',
            header: 'Upcoming Renewals',
            icon: GiBackwardTime,
        },
    ],
    navSubMain: [
        {
            title: 'Leads',
            url: '/leads',
            header: 'All Leads',
            icon: UsersRound,
        },
        {
            title: 'Pre-qual Form',
            url: '/leads/pre-qualification',
            header: 'Pre-qualification',
            icon: ClipboardList,
        },
        {
            title: 'Lead Assignment',
            url: '/leads/assignment',
            header: 'Lead Assignment',
            icon: Shuffle,
        },
        {
            title: 'Personal Schedule',
            url: '/scheduling',
            header: 'Personal Schedule',
            icon: Calendar,
        },
    ],
    navSecondary: [
        {
            title: 'Insights',
            url: '/insights',
            header: 'Insights',
            icon: VscGraph,
        },
        {
            title: 'Payments',
            url: '/payments',
            header: 'Payments',
            icon: MdCreditCard,
        },
        {
            title: 'Properties',
            url: '/properties',
            header: 'All Properties',
            icon: BiBuildings,
        },
    ],
    bottomNav: [
        {
            title: 'Settings',
            url: '/settings',
            header: 'Settings',
            icon: IoSettingsOutline,
        },
        {
            title: 'Get Help',
            url: '/support',
            header: 'Get Help',
            icon: IoHelpBuoyOutline,
        },
        {
            title: 'Tech Support',
            icon: Headset,
            onClick: () => openBrevoChat({ context: { source: 'pm_sidebar' } }),
        },
    ],
    landlordNav: [
        {
            title: 'Applications',
            url: '/landlord/applications',
            header: 'Applications',
            icon: ClipboardList,
        },
        {
            title: 'Leases',
            url: '/leases',
            header: 'All Leases',
            icon: IoDocumentOutline,
        },
    ],
    accountantNav: [
        {
            title: 'Payments',
            url: '/payments',
            header: 'Payments',
            icon: MdCreditCard,
        },
        {
            title: 'Documents',
            url: '/documents',
            header: 'All Documents',
            icon: MdFolderOpen,
        },
    ],
    internalAgentNav: [
        {
            title: 'Showings',
            url: '/showings',
            header: 'Showings',
            icon: RiUserLocationLine,
        },
        {
            title: 'Properties',
            url: '/properties',
            header: 'All Properties',
            icon: BiBuildings,
        },
    ],
};
const getSignDashboardUrl = () => {
    const origin = window.location.origin;
    const url = new URL(origin);
    const parts = url.hostname.split('.');

    parts.unshift('sign'); // ["sign", "staging", "leasehub", "app"]
    const newHost = parts.join('.');

    return `${url.protocol}//${newHost}`;
};
const canManageLeadAssignment = (user) => user?.userType === 'admin' || user?.isOwner;

const withoutLeadAssignment = (items) =>
    items.filter((item) => item.url !== '/leads/assignment');

export function AppSidebar({ ...props }) {
    const api = useContext(ApiContext);
    const location = useLocation();
    const activePage = location.pathname;
    const { user } = React.useContext(UserContext);
    const [unreadMessages, setUnreadMessages] = useState(0);
    const [openGroup, setOpenGroup] = useState(null);
    const { subscribeToNewMessages, subscribeToReadMessage } = useContext(SocketContext);
    const signDashboardURL = getSignDashboardUrl();
    const hostname = window.location.hostname;
    const isLocalhost =
        hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.localhost');
    const SignDashboardNav = {
        title: 'Templates & Documents',
        url: "/documents",
        header: 'Templates & Documents',
        icon: IoDocumentOutline,
    };

    const fetchMessengerUnreadCount = useCallback(async () => {
        try {
            const data = await api.get(`/api/chat/unread_count/?scope=all`);
            setUnreadMessages(data.unreadCount || 0);
        } catch (error) {
            console.log(error);
        }
    }, [api]);

    useEffect(() => {
        fetchMessengerUnreadCount();
    }, [fetchMessengerUnreadCount]);

    useEffect(() => {
        const unsubscribe = subscribeToReadMessage(() => {
            fetchMessengerUnreadCount();
        });

        return () => unsubscribe(); // Cleanup the subscription on unmount
    }, [subscribeToReadMessage, fetchMessengerUnreadCount]);

    useEffect(() => {
        const unsubscribe = subscribeToNewMessages(() => {
            if (!activePage.includes('messaging')) {
                fetchMessengerUnreadCount();
            }
        });

        return () => unsubscribe(); // Cleanup the subscription on unmount
    }, [subscribeToNewMessages, activePage, fetchMessengerUnreadCount]);

    const messaging = URL_MAP.messagingNav.map((nav) => {
        if (nav.title === 'Messaging') {
            return {
                ...nav,
                badge: unreadMessages,
            };
        } else {
            return nav;
        }
    });
    const canSeeLeadAssignment = canManageLeadAssignment(user);
    const leadSubItems = React.useMemo(
        () =>
            canSeeLeadAssignment
                ? URL_MAP.navSubMain
                : withoutLeadAssignment(URL_MAP.navSubMain),
        [canSeeLeadAssignment]
    );
    const leadGroupItems = React.useMemo(
        () =>
            canSeeLeadAssignment
                ? URL_MAP.leadsGroup.children
                : withoutLeadAssignment(URL_MAP.leadsGroup.children),
        [canSeeLeadAssignment]
    );

    const groupItems = React.useMemo(
        () => ({
            leads: leadGroupItems,
            pipeline: URL_MAP.pipelineGroup.children,
            reports: URL_MAP.reportsGroup.children,
        }),
        [leadGroupItems]
    );

    const routeMatchesGroup = useCallback(
        (items) =>
            items.some((item) => {
                const url = item.url;
                return (
                    activePage === url ||
                    (url !== '/' && activePage.startsWith(url.endsWith('/') ? url : `${url}/`))
                );
            }),
        [activePage]
    );

    useEffect(() => {
        const activeGroup = Object.entries(groupItems).find(([, items]) =>
            routeMatchesGroup(items)
        )?.[0];
        if (activeGroup) setOpenGroup(activeGroup);
    }, [groupItems, routeMatchesGroup]);

    const handleGroupOpenChange = useCallback((groupKey) => (nextOpen) => {
        setOpenGroup((currentGroup) => {
            if (nextOpen) return groupKey;
            return currentGroup === groupKey ? null : currentGroup;
        });
    }, []);

    const groupOpenHandlers = React.useMemo(
        () => ({
            leads: handleGroupOpenChange('leads'),
            pipeline: handleGroupOpenChange('pipeline'),
            inventory: handleGroupOpenChange('inventory'),
            reports: handleGroupOpenChange('reports'),
        }),
        [handleGroupOpenChange]
    );

    return (
        <Sidebar collapsible="offcanvas" {...props}>
            <SidebarHeader>
                <SidebarMenu>
                    <SidebarMenuItem>
                        <Logo className="tw-h-10 tw-px-2" />
                    </SidebarMenuItem>
                </SidebarMenu>
            </SidebarHeader>
            <SidebarContent>
                {user?.userType === 'landlord' ? (
                    <>
                        <NavMain items={URL_MAP.landlordNav} />
                    </>
                ) : user?.userType === 'accountant' ? (
                    <>
                        <NavMain header="Accounting" items={URL_MAP.accountantNav} />
                        <NavSecondary items={URL_MAP.bottomNav} className="tw-mt-auto" />
                    </>
                ) : user?.userType === 'internal_agent' ? (
                    <>
                        <NavMain header="Internal Agent" items={URL_MAP.internalAgentNav} />
                        <NavMain header="Leads & CRM" items={leadSubItems} />
                        <NavMain items={messaging} />
                        <NavSecondary items={URL_MAP.bottomNav} className="tw-mt-auto" />
                    </>
                ) : (
                    <>
                        {/* Dashboard kept flat as the home shortcut. */}
                        <NavMain items={URL_MAP.topNav} />

                        {/* Collapsible groups — each expands inline on click,
                            auto-opens when the user is on one of its children.
                            Order mirrors the lifecycle: Leads → Pipeline →
                            Properties → Reports → Messaging. */}
                        <NavCollapsible
                            title={URL_MAP.leadsGroup.title}
                            icon={URL_MAP.leadsGroup.icon}
                            items={leadGroupItems}
                            open={openGroup === 'leads'}
                            onOpenChange={groupOpenHandlers.leads}
                        />
                        <NavCollapsible
                            title={URL_MAP.pipelineGroup.title}
                            icon={URL_MAP.pipelineGroup.icon}
                            items={URL_MAP.pipelineGroup.children}
                            open={openGroup === 'pipeline'}
                            onOpenChange={groupOpenHandlers.pipeline}
                        />
                        <NavCollapsible
                            title={URL_MAP.reportsGroup.title}
                            icon={URL_MAP.reportsGroup.icon}
                            items={URL_MAP.reportsGroup.children}
                            open={openGroup === 'reports'}
                            onOpenChange={groupOpenHandlers.reports}
                        />
                        <NavMain items={messaging} />
                        <NavMain items={[URL_MAP.inventoryGroup]} />
                        <NavMain items={[SignDashboardNav]} />
                        <NavSecondary items={URL_MAP.bottomNav} className="tw-mt-auto" />
                    </>
                )}
                {/* <NavDocuments items={URL_MAP.documents} /> */}
            </SidebarContent>
            <SidebarFooter>
                <NavUser />
            </SidebarFooter>
        </Sidebar>
    );
}
